import { useEffect, type ReactNode } from 'react';
import { Platform, ScrollView, StyleSheet, View, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native';
import { colors } from '~/ui/tokens';
import { Footer } from './Footer';

/**
 * Page shell that keeps the Footer pinned to the bottom of the viewport on
 * short pages while letting tall pages scroll normally. The contentContainer
 * grows to at least the viewport height; the inner wrapper then grows to fill
 * whatever space is left above the Footer, so the Footer never floats up into
 * the middle of a half-empty page (e.g. an empty booking list or the invoice).
 *
 * Screens pass their own content (TopNav + body) as children; the Footer is
 * rendered here so every page gets the same sticky behaviour without repeating
 * the ScrollView boilerplate.
 */
export function Screen({
  children,
  scrollRef,
  nativeID = 'screen-scroll-container',
  id = 'screen-scroll-container',
  onScroll,
}: {
  children: ReactNode;
  scrollRef?: React.RefObject<ScrollView | null>;
  nativeID?: string;
  id?: string;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
}) {
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    let frame = 0;
    const scrollToHash = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (!id) return;
      frame = window.requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('hashchange', scrollToHash);
    };
  }, []);

  return (
    <ScrollView
      ref={scrollRef as any}
      nativeID={nativeID}
      id={id}
      style={styles.screen}
      contentContainerStyle={styles.content}
      onScroll={onScroll}
      scrollEventThrottle={onScroll ? 200 : undefined}
    >
      <View style={styles.grow}>{children}</View>
      <Footer />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.cream,
  },
  content: {
    flexGrow: 1,
  },
  grow: {
    flexGrow: 1,
  },
});
